/**
 * In-memory store for list-restore snapshots and pending-restore flags.
 *
 * Snapshots hold page, sort, scroll anchor, etc. while the user visits a detail
 * view. Pending flags let us restore even when Back arrives as a PUSH (no
 * history idx to pop). Both are cleared on full reload / logout.
 */

import { getHistoryIdx } from "@/routing";

export type ListRestoreSnapshot = {
	/** Clicked row/item id used as the scroll/visibility anchor. */
	anchorId?: string;
	/**
	 * Anchor's offset from the scroll container's visible top when leaving
	 * (`elementRect.top - containerRect.top`). Restored so the item sits in
	 * the same place in the viewport, not merely somewhere on screen.
	 */
	anchorOffset?: number;
	/** True when the anchor was captured via keyboard (Enter/Space). */
	anchorViaKeyboard?: boolean;
	/** How many infinite-query pages were loaded (dashboard alert list). */
	loadedPages?: number;
	/** 1-indexed table page. */
	page?: number;
	pageSize?: number;
	searchQuery?: string;
	sortColumn?: number | string;
	sortDirection?: "ascending" | "descending";
};

type PendingRestore = {
	/** History idx when the row was clicked (list page before navigating away). */
	historyIdx?: number;
};

const isPendingRestoreWithinHistoryWindow = (
	pending: PendingRestore,
): boolean => {
	const { historyIdx } = pending;
	if (historyIdx === undefined) {
		return true;
	}

	const currentIdx = getHistoryIdx();
	if (currentIdx === undefined) {
		return true;
	}

	return Math.abs(currentIdx - historyIdx) <= MAX_PENDING_HISTORY_DELTA;
};

const snapshots = new Map<string, ListRestoreSnapshot>();
/** Set when the user clicks a row so Back can restore even if the return is PUSH. */
const pendingRestoreKeys = new Map<string, PendingRestore>();
/** Per-entity keys (location-sensors:<id>, …) would otherwise grow for the session. */
const MAX_LIST_RESTORE_SNAPSHOTS = 24;
/**
 * Pending restore is valid when returning within this many history steps of the
 * click (list@N → detail@N+1 → back@N or a short in-app detour).
 *
 * Delta 2 also covers HeaderBackButton's `navigate(path)` fallback, which is a
 * PUSH to N+2 and must still restore. The same window means a sidebar click
 * back to the same list within two steps of a row click also restores — by
 * design, and indistinguishable from that Back fallback.
 */
const MAX_PENDING_HISTORY_DELTA = 2;

export const clearAllListRestoreSnapshots = (): void => {
	snapshots.clear();
	pendingRestoreKeys.clear();
};

export const clearListRestorePending = (listKey: string): void => {
	pendingRestoreKeys.delete(listKey);
};

export const clearListRestoreSnapshot = (listKey: string): void => {
	snapshots.delete(listKey);
	pendingRestoreKeys.delete(listKey);
};

export const getListRestoreSnapshot = (
	listKey: string,
): ListRestoreSnapshot | undefined => snapshots.get(listKey);

export const hasListRestorePending = (listKey: string): boolean => {
	const pending = pendingRestoreKeys.get(listKey);
	if (!pending) {
		return false;
	}

	return isPendingRestoreWithinHistoryWindow(pending);
};

export const pruneExpiredListRestorePending = (listKey: string): void => {
	const pending = pendingRestoreKeys.get(listKey);
	if (!pending) {
		return;
	}

	if (!isPendingRestoreWithinHistoryWindow(pending)) {
		pendingRestoreKeys.delete(listKey);
	}
};

export const hasListRestoreSnapshot = (listKey: string): boolean =>
	snapshots.has(listKey);

/** Call when the user clicks a list row so returning via PUSH still restores. */
export const markListRestorePending = (listKey: string): void => {
	pendingRestoreKeys.set(listKey, { historyIdx: getHistoryIdx() });
};

export const saveListRestoreSnapshot = (
	listKey: string,
	snapshot: ListRestoreSnapshot,
): void => {
	snapshots.delete(listKey);
	snapshots.set(listKey, snapshot);
	while (snapshots.size > MAX_LIST_RESTORE_SNAPSHOTS) {
		const oldest = snapshots.keys().next().value;
		if (oldest === undefined || oldest === listKey) {
			break;
		}
		snapshots.delete(oldest);
		pendingRestoreKeys.delete(oldest);
	}
};
